import React from "react";
import { SITUATIONAL_TAGS, TAG_COLORS } from "../utils/situationalTags";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

/**
 * Single situational tag badge with tooltip and optional stats
 */
export const SituationalTagBadge = ({ tag, size = "sm", stats = null }) => {
  const tagInfo = SITUATIONAL_TAGS[tag];
  if (!tagInfo) return null;
  
  const sizeClasses = {
    xs: "text-[10px] px-1.5 py-0.5",
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1"
  };
  
  // Format profit display
  const formatProfit = (profit) => {
    if (profit === undefined || profit === null) return null;
    const sign = profit >= 0 ? '+' : '';
    return `${sign}$${Math.abs(profit).toLocaleString()}`;
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`inline-flex items-center gap-1.5 font-mono font-bold rounded border cursor-help ${TAG_COLORS[tag]} ${sizeClasses[size]}`}
            data-testid={`tag-${tag}`}
          >
            [{tag}]
            {stats && stats.total_picks > 0 && (
              <span className="flex items-center gap-1 text-[10px] font-semibold">
                <span className="text-white/80">{stats.wins}-{stats.losses}</span>
                <span className={stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {formatProfit(stats.profit)}
                </span>
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-zinc-900 border-zinc-700">
          <div className="text-sm">
            <p className="font-bold text-white">{tagInfo.name}</p>
            <p className="text-zinc-400 text-xs max-w-[200px]">{tagInfo.description}</p>
            {stats && stats.total_picks > 0 && (
              <div className="mt-2 pt-2 border-t border-zinc-700 text-xs">
                <p className="text-zinc-300">Record: <span className="font-bold">{stats.wins}-{stats.losses}</span></p>
                <p className="text-zinc-300">Win Rate: <span className={`font-bold ${stats.win_rate >= 55 ? 'text-green-400' : 'text-zinc-300'}`}>{stats.win_rate}%</span></p>
                <p className="text-zinc-300">Profit: <span className={`font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatProfit(stats.profit)}</span></p>
                <p className="text-zinc-300">ROI: <span className={`font-bold ${stats.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{stats.roi}%</span></p>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Display multiple situational tags inline with stats
 */
export const SituationalTagList = ({ tags = [], size = "sm", showLabel = true, tagStats = {} }) => {
  if (!tags || tags.length === 0) return null;
  
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {showLabel && (
        <span className="text-zinc-500 text-xs mr-1">Edge Stack:</span>
      )}
      {tags.map(tag => (
        <SituationalTagBadge 
          key={tag} 
          tag={tag} 
          size={size} 
          stats={tagStats[tag]}
        />
      ))}
    </div>
  );
};

/**
 * Legend showing all available tags
 */
export const SituationalTagLegend = ({ compact = false }) => {
  const tags = Object.values(SITUATIONAL_TAGS);
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
        {tags.map(tag => (
          <span key={tag.code} className="flex items-center gap-1">
            <span className={`font-mono font-bold ${TAG_COLORS[tag.code].split(' ')[1]}`}>[{tag.code}]</span>
            <span>{tag.name}</span>
          </span>
        ))}
      </div>
    );
  }
  
  return (
    <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-white mb-3">Situational Edge Tags</h4>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {tags.map(tag => (
          <div key={tag.code} className="flex items-start gap-2">
            <span className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded border ${TAG_COLORS[tag.code]}`}>
              [{tag.code}]
            </span>
            <div>
              <p className="text-white text-xs font-medium">{tag.name}</p>
              <p className="text-zinc-500 text-[10px]">{tag.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SituationalTagBadge;
